import { useState, useMemo } from 'react';
import DOMPurify from 'dompurify';
import {
  MapPin,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Mail,
  Phone,
  Sparkles,
  Loader2,
  AlertTriangle
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SkillBadge } from '@/components/ui/skill-badge';
import { formatDateRange, formatDate } from '@/lib/format';
import { useMyProfileId } from '@/hooks/use-my-profile-id';
import { useMatchAssignment, useMyMatchForAssignment } from '@/hooks/use-matches';
import { useSaveAssignment } from '@/hooks/use-saved-assignments';
import type { Assignment } from '@/hooks/use-assignments';
import { MatchDetailDialog } from '@/components/matches/match-detail-dialog';
import { CheckCircle2 } from 'lucide-react';

interface AssignmentCardProps {
  assignment: Assignment;
  mySkills?: string[];
}

const statusColors: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  closed: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  draft: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
};

const PREVIEW_SKILL_COUNT = 5;

export function AssignmentCard({ assignment, mySkills }: AssignmentCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [openMatchDialog, setOpenMatchDialog] = useState(false);
  const profileId = useMyProfileId();
  const matchMe = useMatchAssignment();
  const saveAssignment = useSaveAssignment();
  const existingMatch = useMyMatchForAssignment(assignment.id).data?.data ?? null;
  const hasMatch = !!existingMatch;

  const sd = assignment.sourceData;
  const contact = sd?.contact ?? null;
  const previewSkills = assignment.skills.slice(0, PREVIEW_SKILL_COUNT);

  const skillOverlap = useMemo(() => {
    if (!mySkills?.length || !assignment.skills.length) return 0;
    const mySet = new Set(mySkills);
    return assignment.skills.filter((s) => mySet.has(s.skillNormalized)).length;
  }, [mySkills, assignment.skills]);

  return (
    <Card
      className="cursor-pointer transition-colors hover:bg-muted/30"
      onClick={() => setExpanded((e) => !e)}
    >
      <CardContent className="p-4">
        {/* Collapsed header */}
        <div className="flex items-start justify-between">
          <div className="flex-1 space-y-1.5">
            <h3 className="text-sm font-semibold">{assignment.title}</h3>

            <div className="flex flex-wrap items-center gap-2">
              {assignment.location && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  {assignment.location}
                </span>
              )}

              {sd?.company && <span className="text-xs text-muted-foreground">{sd.company}</span>}

              {sd?.duration && <span className="text-xs text-muted-foreground">{sd.duration}</span>}

              {sd?.rate && (
                <span className="text-xs font-medium text-orange-600 dark:text-orange-400">
                  {sd.rate}
                </span>
              )}

              <Badge
                variant="outline"
                className={
                  statusColors[assignment.status] ??
                  'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                }
              >
                {assignment.status}
              </Badge>

              {(sd?.platform || assignment.source) && (
                <Badge
                  variant="outline"
                  className="bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                >
                  {sd?.platform ?? assignment.source}
                </Badge>
              )}

              {assignment.publishedAt && (
                <span className="text-xs text-muted-foreground">
                  {formatDate(assignment.publishedAt)}
                </span>
              )}
            </div>

            {previewSkills.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-0.5">
                {previewSkills.map((s) => (
                  <SkillBadge
                    key={s.id}
                    skill={s.skill}
                    variant={s.isMustHave ? 'match' : 'neutral'}
                  />
                ))}
                {assignment.skills.length > PREVIEW_SKILL_COUNT && (
                  <span className="self-center text-xs text-muted-foreground">
                    +{assignment.skills.length - PREVIEW_SKILL_COUNT} more
                  </span>
                )}
                {skillOverlap > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                    {skillOverlap}/{assignment.skills.length} skills match
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 ml-2">
            <Button
              size="sm"
              variant="outline"
              disabled={!profileId || matchMe.isPending}
              onClick={(e) => {
                e.stopPropagation();
                if (hasMatch) {
                  setOpenMatchDialog(true);
                  return;
                }
                if (profileId) {
                  matchMe.mutate({ profileId, assignmentId: assignment.id });
                  saveAssignment.mutate({ assignmentId: assignment.id });
                }
              }}
              className="shrink-0"
            >
              {matchMe.isPending ? (
                <>
                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  Matching...
                </>
              ) : hasMatch ? (
                <>
                  <CheckCircle2 className="mr-1 h-3 w-3 text-green-600 dark:text-green-400" />
                  Matched
                </>
              ) : (
                <>
                  <Sparkles className="mr-1 h-3 w-3" />
                  Match Me
                </>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={saveAssignment.isPending}
              onClick={(e) => {
                e.stopPropagation();
                saveAssignment.mutate({ assignmentId: assignment.id });
              }}
              className="shrink-0"
            >
              {saveAssignment.isPending
                ? 'Saving...'
                : saveAssignment.isSuccess
                  ? '✓ Saved'
                  : 'Save'}
            </Button>
            <div className="text-muted-foreground">
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </div>
          </div>
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="mt-4 space-y-4 border-t pt-4" onClick={(e) => e.stopPropagation()}>
            {/* Full description */}
            {(sd?.fullDescription || assignment.description) &&
              (() => {
                const rawText = sd?.fullDescription ?? assignment.description ?? '';
                const hasHtmlTags = /<(?:p|div|br|ul|ol|li|h[1-6])\b/i.test(rawText);

                // For content that already has HTML structure, use as-is
                // For plain text, convert to readable HTML with headers and line breaks
                const formatted = hasHtmlTags
                  ? rawText
                  : rawText
                      // Bold section headers: "Word(s):" at start or after line break
                      .replace(
                        /(?:^|(?<=\.\s)|(?<=<br\/?>\s*))([A-ZÅÄÖ][A-Za-zåäöÅÄÖ\s\-/&]{1,40})\s*:/g,
                        '<br/><br/><strong>$1:</strong>'
                      )
                      // Line break before bullets
                      .replace(/\s*([•·]\s)/g, '<br/>$1')
                      // Line break before emoji indicators
                      .replace(/\s*(✔️|✅|❌|⚠️|&#x[\da-f]+;)/gi, '<br/>$1')
                      // Paragraph break between sentences
                      .replace(/([.!?])\s{2,}([A-ZÅÄÖ])/g, '$1<br/><br/>$2')
                      .replace(/([.!?])\s+([A-ZÅÄÖ][a-zåäö])/g, '$1<br/><br/>$2')
                      // Clean up leading breaks
                      .replace(/^(<br\s*\/?>)+/, '');
                return (
                  <div>
                    <h4 className="mb-2 text-sm font-semibold">Description</h4>
                    <div
                      className="prose prose-sm max-w-none text-foreground [&_strong]:font-semibold [&_br]:block [&_br]:mt-1"
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(formatted)
                      }}
                    />
                  </div>
                );
              })()}

            {/* Metadata grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
              {assignment.location && (
                <div>
                  <span className="text-muted-foreground">Location</span>
                  <p className="font-medium">{assignment.location}</p>
                </div>
              )}
              {sd?.company && (
                <div>
                  <span className="text-muted-foreground">Company</span>
                  <p className="font-medium">{sd.company}</p>
                </div>
              )}
              {(sd?.startDate || sd?.endDate) && (
                <div>
                  <span className="text-muted-foreground">Period</span>
                  <p className="font-medium">{formatDateRange(sd.startDate, sd.endDate)}</p>
                </div>
              )}
              {sd?.duration && (
                <div>
                  <span className="text-muted-foreground">Duration</span>
                  <p className="font-medium">{sd.duration}</p>
                </div>
              )}
              {sd?.scope && (
                <div>
                  <span className="text-muted-foreground">Scope</span>
                  <p className="font-medium">{sd.scope}</p>
                </div>
              )}
              {sd?.rate && (
                <div>
                  <span className="text-muted-foreground">Rate</span>
                  <p className="font-medium text-orange-600">{sd.rate}</p>
                </div>
              )}
              {sd?.level && (
                <div>
                  <span className="text-muted-foreground">Level</span>
                  <p className="font-medium">{sd.level}</p>
                </div>
              )}
              {sd?.remoteness && (
                <div>
                  <span className="text-muted-foreground">Remote</span>
                  <p className="font-medium">{sd.remoteness}</p>
                </div>
              )}
            </div>

            {/* All skills */}
            {assignment.skills.length > 0 && (
              <div>
                <h4 className="mb-2 text-sm font-semibold">Skills Required</h4>
                <div className="flex flex-wrap gap-1.5">
                  {assignment.skills.map((s) => (
                    <SkillBadge
                      key={s.id}
                      skill={s.skill}
                      variant={s.isMustHave ? 'match' : 'neutral'}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Source link */}
            {sd?.url && (
              <a
                href={sd.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3 w-3" />
                View on {sd.platform ?? assignment.source}
              </a>
            )}

            {/* Contact person */}
            {contact && (contact.name || contact.email || contact.phone) && (
              <div className="rounded-lg border p-3">
                <h4 className="mb-2 text-xs font-semibold">Contact</h4>
                <div className="flex items-center gap-3">
                  {contact.profilePictureUrl && (
                    <img
                      src={contact.profilePictureUrl}
                      alt={contact.name ?? 'Contact'}
                      className="h-9 w-9 rounded-full object-cover"
                    />
                  )}
                  <div className="space-y-0.5 text-xs">
                    {contact.name && <p className="font-medium">{contact.name}</p>}
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Mail className="h-3 w-3" />
                        {contact.email}
                      </a>
                    )}
                    {contact.phone && (
                      <p className="flex items-center gap-1 text-muted-foreground">
                        <Phone className="h-3 w-3" />
                        {contact.phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {matchMe.data?.data?.aiError && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 p-3 text-xs text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div>
              <p className="font-semibold">AI analysis failed — heuristic match saved.</p>
              <p className="mt-0.5 opacity-90">{matchMe.data.data.aiError}</p>
            </div>
          </div>
        )}
      </CardContent>
      {existingMatch && (
        <MatchDetailDialog
          matchId={openMatchDialog ? existingMatch.id : null}
          onClose={() => setOpenMatchDialog(false)}
          assignmentTitle={assignment.title}
        />
      )}
    </Card>
  );
}
